// pages/overlay/StartingSoon.jsx
// Pre-stream holding screen — dawn over the griddle, on Break's skeleton:
// left column / full-height transparent square video center / right column.
// Columns carry the sunrise identity (navy melting into gold/orange/pink);
// content is deliberately sparse — brand + Starting Soon badge + flash menu
// on the left, Gazette + ONE proof card (queue OR recent creations) on the
// right. The 2026-06 declutter cut character quotes/badges, the hire-us
// badge, showcase metadata, and the measured clip-path video hole.

import { useEffect, useRef, useState } from "react";
import { collection, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./../../firebase";
import useOrientation from "./../../hooks/useOrientation";
import SummonBanner from "./../../components/overlay/SummonBanner";
import useStreamConfig from "./../../hooks/useStreamConfig";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";
import useFlashDesigns from "./../../hooks/useFlashDesigns";
import useObsActive from "./../../hooks/useObsActive";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useBoothShowcase from "./../../hooks/useBoothShowcase";
import { filterMarqueeForShift } from "./../../utils/marqueeContext";
import { computeDisplayTitle } from "./../../utils/meditations";
import { computeQueueWait, fmtWait } from "./../../utils/overlayFormat";
import MarqueeTicker from "./../../components/overlay/MarqueeTicker";
import QueueCard from "./../../components/overlay/QueueCard";
import JoPLogo from "./../../components/overlay/JoPLogo";
import SparkleField from "./../../components/overlay/SparkleField";
import FlashDesignsPanel from "./../../components/overlay/FlashDesignsPanel";
import PromoPanel from "./../../components/overlay/PromoPanel";
import { FlashBrandBand, IdleCaptionBar, IdleQueueChip } from "./../../components/overlay/IdleSceneKit";

const DISPLAY = "var(--jop-display)";
const CREAM = "var(--jop-cream)";
const BLUE = "var(--jop-blue)";
const GOLD = "var(--jop-gold)";
const SKY = "var(--jop-highlight-blue)";

// Column width: (viewport - square) / 2 capped at 18vw — same as Break (the
// warm-up reel behind the center window is the draw; 345px columns at 1080p
// still fit every panel).
const COL_W = "min(calc((100vw - 100vh) / 2), 18vw)";

// ── DAWN BACKGROUND ─────────────────────────────────────────────────────────
// First light: deep navy up top giving way to a sunrise glow along the bottom
// horizon. Static smooth gradients ONLY — the old gingham tile's low-contrast
// squares bloomed into visible gray patches on the 2000-nit outdoor booth TV.
// Every stop rides a --jop-*-rgb var so event themes recolor the sky.
const BG_BASE = "var(--jop-bg-warm-navy)";
const DAWN_BG = `
  radial-gradient(ellipse 130% 60% at 50% 112%, rgba(var(--jop-gold-rgb), 0.34) 0%, rgba(var(--jop-orange-rgb), 0.22) 34%, rgba(var(--jop-pink-rgb), 0.10) 58%, transparent 76%),
  linear-gradient(180deg, transparent 52%, rgba(var(--jop-pink-rgb), 0.07) 74%, rgba(var(--jop-orange-rgb), 0.12) 100%),
  linear-gradient(180deg, rgba(var(--jop-ink-rgb), 0.55) 0%, rgba(var(--jop-navy-rgb), 0.10) 46%, transparent 64%)
`;

// ── RECENT CREATIONS DATA ───────────────────────────────────────────────────
const ROTATE_MS = 10000;

function useShowcasePancakes(authed) {
  const [pancakes, setPancakes] = useState([]);
  const shuffledRef = useRef(false);
  useEffect(() => {
    if (!authed) return;
    const q = query(collection(db, "orders"), where("state", "==", "done"), limit(80));
    return onSnapshot(q, (snap) => {
      const results = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(o => (o.photo_cutout_url || o.cutoutThumbnailUrl) && o.galleryPrivacy !== "private" && !o.hidden && !o.trashedAt)
        .sort((a, b) => (b.readyToDeliverAt?.toMillis?.() ?? 0) - (a.readyToDeliverAt?.toMillis?.() ?? 0));
      // Shuffle only on first load to keep stable order across updates
      if (!shuffledRef.current) {
        results.sort(() => 0.5 - Math.random());
        shuffledRef.current = true;
      }
      setPancakes(results.slice(0, 15));
    });
  }, [authed]);
  return pancakes;
}

// ── PANCAKE SHOWCASE — image + title + artist only ──────────────────────────
// Landscape-only since 2026-08-29 (the portrait floating card blanketed the
// warm-up reel, which showcases the work itself). Sized for the 18vw column.
function PancakeShowcase({ pancakes }) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("visible");

  useEffect(() => {
    if (pancakes.length <= 1) return;
    const t = setInterval(() => {
      const nextIdx = (idx + 1) % pancakes.length;
      const nextSrc = pancakes[nextIdx]?.cutoutThumbnailUrl || pancakes[nextIdx]?.photo_cutout_url;
      if (nextSrc) { const img = new Image(); img.src = nextSrc; }
      setPhase("fading");
      setTimeout(() => {
        setIdx(nextIdx);
        setPhase("swapping");
        requestAnimationFrame(() => { requestAnimationFrame(() => setPhase("visible")); });
      }, 500);
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [pancakes.length, idx]);

  if (pancakes.length === 0) return null;
  const p = pancakes[idx % pancakes.length];
  const src = p.cutoutThumbnailUrl || p.photo_cutout_url;
  const title = computeDisplayTitle(p) || p.subjectName || "Untitled";
  const artist = p.artistName || "";

  return (
    <div style={{
      display: "flex", flexDirection: "row", alignItems: "center",
      gap: "1rem", padding: "1rem 1.1rem",
      opacity: phase === "visible" ? 1 : 0,
      transition: phase === "swapping" ? "none" : "opacity 0.5s ease",
      // Fill the rigid card exactly — content variation clips inside instead
      // of growing the card and reflowing the Gazette above it.
      width: "100%", height: "100%", boxSizing: "border-box",
    }}>
      {src && (
        <img src={src} alt="" style={{
          width: "140px", height: "140px",
          objectFit: "contain", flexShrink: 0,
          filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.5))",
        }} />
      )}
      <div style={{
        flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
        gap: "0.4rem", justifyContent: "center", overflow: "hidden",
      }}>
        <div style={{
          color: GOLD, fontWeight: 700, fontFamily: DISPLAY,
          fontSize: "1.4rem", lineHeight: 1.25,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>"{title}"</div>
        {artist && (
          <div style={{
            color: "var(--jop-highlight-blue)", fontWeight: 700,
            fontSize: "1.1rem",
          }}>by {artist}</div>
        )}
      </div>
    </div>
  );
}

// ── AUTO-SCROLLING QUEUE LIST ───────────────────────────────────────────────
function AutoScrollQueue({ orders }) {
  const ref = useRef(null);
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const overflows = el.scrollHeight > el.clientHeight + 10;
    setNeedsScroll(overflows);
    if (!overflows) return;
    let frame, pos = 0;
    const speed = 0.5;
    function tick() {
      pos += speed;
      if (pos >= el.scrollHeight - el.clientHeight) pos = 0;
      el.scrollTop = pos;
      frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [orders.length]);

  return (
    <div ref={ref} style={{
      flex: 1, overflowY: "hidden", display: "flex", flexDirection: "column", gap: "0.5rem",
      ...(needsScroll ? {
        maskImage: "linear-gradient(to bottom, transparent 0%, black 5%, black 90%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 5%, black 90%, transparent 100%)",
      } : {}),
    }}>
      {orders.map((order) => (
        <QueueCard key={order.id} order={order} variant="compact" />
      ))}
    </div>
  );
}

// ── COMING UP CARD — rigid bottom slot variant ──────────────────────────────
function ComingUpCard({ orders, height }) {
  return (
    <div style={{
      background: "rgba(0,0,0,0.2)",
      border: "var(--jop-card-border)",
      boxShadow: "var(--jop-card-glow)",
      borderRadius: "16px", padding: "0.8rem 0.9rem",
      height, boxSizing: "border-box",
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      <div style={{
        color: GOLD, fontWeight: 700, fontFamily: DISPLAY,
        fontSize: "1.3rem", textTransform: "uppercase", letterSpacing: "0.08em",
        display: "flex", alignItems: "center", gap: "0.6rem",
        marginBottom: "0.6rem", flexShrink: 0,
      }}>
        <span>🎟 Coming Up</span>
        <span style={{
          background: BLUE, color: "white", fontFamily: DISPLAY,
          fontSize: "1rem", padding: "2px 12px",
          borderRadius: "999px", fontWeight: 700,
        }}>{orders.length}</span>
      </div>
      <AutoScrollQueue orders={orders} />
    </div>
  );
}

// ── STARTING SOON BADGE — analog of Break's BRB badge ───────────────────────
function StartingSoonBadge({ config, active, portrait, accent = GOLD, accentRgb = "var(--jop-gold-rgb)" }) {
  return (
    <div style={{
      background: CREAM, color: BLUE,
      fontFamily: DISPLAY, textAlign: "center",
      padding: "0.9rem 1.1rem", borderRadius: "16px",
      border: `3px solid ${accent}`,
      boxShadow: `0 0 22px rgba(${accentRgb}, 0.28)`,
      flexShrink: 0, position: "relative", zIndex: 1,
      animation: "jopSoonPulse 3s ease-in-out infinite",
      animationPlayState: active ? "running" : "paused",
    }}>
      <div style={{
        fontWeight: 700, fontSize: portrait ? "1.7rem" : "1.45rem",
        textTransform: "uppercase", letterSpacing: "0.1em", lineHeight: 1.2,
      }}>✦ Starting Soon ✦</div>
      <div style={{
        color: "var(--jop-navy)", fontWeight: 700,
        fontSize: portrait ? "1.35rem" : "1.15rem", marginTop: "0.35rem", lineHeight: 1.25,
      }}>{config.streamTitle || "Live Pancake Art"}</div>
      <div style={{
        fontSize: portrait ? "1.1rem" : "0.9rem", fontWeight: 600,
        opacity: 0.7, marginTop: "0.2rem", lineHeight: 1.3,
      }}>{config.streamSubtitle || "The griddle is warming up"}</div>
    </div>
  );
}

// (The old CustomQrCard lived here — it rendered its QR via an EXTERNAL image
// API (api.qrserver.com), which the untrusted booth hotspot rule forbids.
// Replaced 2026-07-07 by the flow-resolved SummonBanner layer on the feed —
// local qrcode.react, custom-URL flow keeps the Stream Setup label copy.)

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function StartingSoon() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const active = useObsActive();
  const config = useStreamConfig();
  const { drawing, cookingAll, queued, allActive, shiftLoaded, activeShift, concurrentCount } = useCurrentShiftOrders();
  const showcasePancakes = useShowcasePancakes(shiftLoaded);
  const { allDesigns: designs } = useFlashDesigns(activeShift, { previewWhenIdle: true });
  // Marketing spotlight — same rotating boothShowcase queue as Booth/Break
  // (respects per-shift disabledShowcaseIds).
  const { queue: spotlightQueue } = useBoothShowcase();
  // Per-shift override layer ("both" mode = no context filter, override only).
  const filteredMarquee = filterMarqueeForShift(config.marqueeMessages, "both", activeShift);

  const showQueue = config.queueVisibleOnStartingScreen !== false;
  // Ordering QR: the flow-resolved SummonBanner layer on the feed region.
  // Legacy gates (showJoinLinkOnStartingScreen + customQrUrl landscape card,
  // isQueueOpen + livestreamJoinEnabled portrait CTA) are reconstructed by
  // resolveQrFlow() for shifts without qrFlows — pre-show this lights up the
  // moment the operator opens the queue + ordering.
  const joinQueueDepth = [drawing, ...(cookingAll || []), ...(queued || [])].filter(Boolean).length;
  const joinWaitMin = computeQueueWait(allActive, activeShift);
  const comingUp = showQueue
    ? [...allActive, ...queued].filter((o, i, arr) => arr.findIndex(x => x.id === o.id) === i)
    : [];

  // ONE proof card in the bottom-right slot: live tickets win over recent
  // creations — never both, that was half the old clutter.
  const showComingUp = showQueue && comingUp.length > 0;
  const showShowcase = !showComingUp && showcasePancakes.length > 0;
  const bottomCardHeight = "clamp(250px, 30vh, 340px)";

  const columnStyle = (side) => ({
    position: "absolute", top: 0, bottom: 0, [side]: 0,
    width: COL_W,
    background: BG_BASE, backgroundImage: DAWN_BG,
    // Gold inner edge, matching Break
    [side === "left" ? "borderRight" : "borderLeft"]: `3px solid ${GOLD}`,
    boxShadow: `inset ${side === "left" ? "-8px" : "8px"} 0 20px rgba(var(--jop-gold-rgb), 0.08)`,
    display: "flex", flexDirection: "column",
    justifyContent: "space-between",
    gap: "1rem",
    padding: "1.4rem 1.1rem",
    zIndex: 2, overflow: "hidden",
  });

  return (
    <div
      data-orientation={orientation}
      style={{
        width: "100vw", height: "100vh",
        background: "transparent",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: "calc(18px + 0.7vw)",
        userSelect: "none", overflow: "hidden", position: "relative",
      }}
    >

      {/* Ordering QR — flow-resolved SummonBanner layer, anchored to the
          feed region (same contract as Break): portrait floats below the
          pill with its floor at the IG safe-zone bottom (y≈1320); landscape
          uses the center camera square between the columns. */}
      <div style={portrait
        ? { position: "absolute", left: 0, right: 0, top: "280px", bottom: "31.5vh", zIndex: 3 }
        : { position: "absolute", top: 0, bottom: 0, left: COL_W, right: COL_W, zIndex: 3 }
      }>
        <SummonBanner
          scene="startingSoon"
          shift={activeShift}
          concurrentCount={concurrentCount}
          streamConfig={config}
          queueDepth={joinQueueDepth}
          waitMin={joinWaitMin}
          portrait={portrait}
          active={active}
        />
      </div>

      {/* ═══ LEFT COLUMN: brand + badge + flash menu + marquee ═════════════ */}
      {!portrait && (
        <div style={columnStyle("left")}>
          <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <SparkleField density={10} seed={42} />
          </div>

          {/* 1. Logo — 130 (was 160) for the narrowed 18vw column */}
          <div style={{ textAlign: "center", flexShrink: 0, position: "relative", zIndex: 1 }}>
            <JoPLogo variant="wide" height={130} animated />
          </div>

          {/* 2. Starting Soon badge — stream title + subtitle live inside */}
          <StartingSoonBadge config={config} active={active} portrait={false} />

          {/* 3. Flash menu — what's on offer before the queue opens */}
          <div style={{
            background: "rgba(0,0,0,0.2)",
            border: "1px solid rgba(var(--jop-gold-rgb), 0.25)",
            borderRadius: "14px", padding: "0.8rem 0.9rem",
            flex: 1, minHeight: 0,
            display: "flex", flexDirection: "column",
            position: "relative", zIndex: 1, overflow: "hidden",
          }}>
            {designs && designs.length > 0 ? (
              <FlashDesignsPanel designs={designs} portrait={false} />
            ) : (
              <div style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                color: CREAM, fontFamily: DISPLAY, fontSize: "1rem",
                opacity: 0.45, textAlign: "center",
              }}>Fresh pancake art, made to order</div>
            )}
          </div>

          {/* 4. Marquee — rigid slot so messages never reflow the menu */}
          <div style={{
            flexShrink: 0, position: "relative", zIndex: 1,
            height: "clamp(64px, 9vh, 110px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            textAlign: "center", overflow: "hidden",
          }}>
            {filteredMarquee && (
              <MarqueeTicker messages={filteredMarquee} fontSize="1.4rem" color={CREAM} />
            )}
          </div>
        </div>
      )}

      {/* ═══ CENTER: full-height transparent square for the camera ═════════ */}

      {/* ═══ RIGHT COLUMN: QR (optional) + Gazette + one proof card ════════ */}
      {!portrait && (
        <div style={columnStyle("right")}>
          <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
            <SparkleField density={10} seed={77} />
          </div>

          {/* 1. Marketing spotlight — rotating Griddle Gazette (the custom
              QR card moved onto the feed — SummonBanner layer above) */}
          <div style={{
            flex: 1, minHeight: 0,
            display: "flex", flexDirection: "column",
            position: "relative", zIndex: 1,
          }}>
            <PromoPanel queue={spotlightQueue} />
          </div>

          {/* 3. ONE rigid proof card: live queue beats recent creations */}
          {showComingUp && (
            <div style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
              <ComingUpCard orders={comingUp} height={bottomCardHeight} />
            </div>
          )}
          {showShowcase && (
            <div style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
              <div style={{
                color: CREAM, fontWeight: 700, fontFamily: DISPLAY,
                fontSize: "1.1rem", textTransform: "uppercase", opacity: 0.45,
                letterSpacing: "0.1em", marginBottom: "0.5rem",
              }}>Recent Creations</div>
              <div style={{
                background: "rgba(var(--jop-cream-rgb), 0.04)",
                border: "var(--jop-card-border)",
                boxShadow: "var(--jop-card-glow)",
                borderRadius: "16px",
                // Rigid footprint — the card never grows with content, so the
                // Gazette above keeps a stable slice of the column.
                height: bottomCardHeight,
                boxSizing: "border-box", overflow: "hidden",
              }}>
                <PancakeShowcase pancakes={showcasePancakes} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ PORTRAIT (1080×1920) — IG Live idle cut, sibling of Break ═════
          Sizzle reel / warm-up cam runs full-screen BEHIND this overlay and
          it IS the show — the 2026-08-29 declutter cut the mid-stack Recent
          Creations card + TickerPill so the reel's midfield (~y540–1350)
          stays open (the reel showcases the work itself). Compact top group
          below the IG host-bar zone, ordering QR on the feed (SummonBanner
          layer above), IdleCaptionBar riding the flash band's top edge
          (marquee + URL — below the IG-safe floor by design, the booth panel
          shows it in full), flash brand band at the bottom (geometry
          untouched — the grid never moves on a scene cut). SKY accents where
          Break uses gold — same silhouette, different hour of the day. */}
      {portrait && (
        <>
          {/* Top group — brand lockup, Starting Soon badge, queue chip.
              Compact: ends ~y560. Sibling geometry to Break's; SKY accents. */}
          <div style={{
            position: "absolute", top: "270px", left: 0, right: 0,
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: "0.7rem",
            zIndex: 2, pointerEvents: "none",
          }}>
            <JoPLogo variant="wide" height={110} animated />
            <div style={{ width: "min(660px, calc(100vw - 200px))" }}>
              <StartingSoonBadge
                config={config}
                active={active}
                portrait
                accent={SKY}
                accentRgb="var(--jop-highlight-blue-rgb)"
              />
            </div>
            <IdleQueueChip
              open={!!activeShift?.isQueueOpen}
              queueDepth={joinQueueDepth}
              waitMin={joinWaitMin}
              fmtWait={fmtWait}
              accent={SKY}
              closedText="✨ The queue opens soon"
            />
          </div>

          {/* Caption bar — the ONE rotating text surface: per-shift marquee
              + the URL beat, pinned above the band. */}
          <IdleCaptionBar
            messages={filteredMarquee}
            accentRgb="var(--jop-highlight-blue-rgb)"
          />

          {/* Band logo + URL row dropped — the lockup and TickerPill above
              carry brand + messaging; the band is all flash grid now (the
              52px shave keeps the grid box pixel-identical, matching Booth). */}
          <FlashBrandBand
            designs={designs}
            active={active}
            accent={SKY}
            showLogo={false}
            showUrl={false}
          />
        </>
      )}

      <style>{`
        @keyframes jopSoonPulse {
          /* Scale-breath, not animated box-shadow — shadow is a paint
             property and repaints every frame; transform composites free. */
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.025); }
        }
      `}</style>
    </div>
  );
}
